import { useDisclosure } from "@mantine/hooks"
import { PaginationState } from "@tanstack/react-table"
import { useMemo, useState, useCallback } from "react"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import type { FindProductionSitesForSupplierResponseDto } from "~/gen/api"
import { useSupplierProductionSitesColumns } from "~/pages/SupplierPortal/ProductionSites/columns"
import { DeleteProductionSiteModal } from "~/pages/SupplierPortal/ProductionSites/components/DeleteProductionSiteModal"
import { EditProductionSiteDrawer } from "~/pages/SupplierPortal/ProductionSites/components/EditProductionSiteDrawer"
import { useAuth } from "~/store/auth-context"

type Props = {
  productionSites: FindProductionSitesForSupplierResponseDto[]
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  pagination: PaginationState
  pageCount: number
  totalCount?: number
}

export const ProductionSiteTable = ({
  productionSites,
  setPagination,
  pagination,
  pageCount = 0,
  totalCount = 0,
}: Props) => {
  const { isAdmin } = useAuth()
  const [openedDelete, { open: openDelete, close: closeDelete }] =
    useDisclosure()
  const [editedProductionSite, setEditedProductionSite] = useState<
    FindProductionSitesForSupplierResponseDto | undefined
  >(undefined)
  const [deletedProductionSite, setDeletedProductionSite] = useState<
    FindProductionSitesForSupplierResponseDto | undefined
  >(undefined)

  const onEdit = useCallback(
    (site: FindProductionSitesForSupplierResponseDto) => {
      setEditedProductionSite(site)
    },
    []
  )

  const onDelete = useCallback(
    (site: FindProductionSitesForSupplierResponseDto) => {
      setDeletedProductionSite(site)
      openDelete()
    },
    [openDelete]
  )

  const columns = useSupplierProductionSitesColumns({
    isAdmin,
    onEdit,
    onDelete,
  })

  const filteredData = useMemo(
    () => productionSites.filter((site) => site.deletedAt === null || isAdmin),
    [productionSites, isAdmin]
  )

  const table = useCustomReactTable({
    data: filteredData,
    columns,
    initialColumnPinning: { left: ["name"] },
    ...(setPagination !== undefined &&
      pagination !== undefined && {
        state: { pagination },
        pageCount,
        manualPagination: true,
        onPaginationChange: setPagination,
      }),
  })

  return (
    <>
      <CustomTable
        table={table}
        pagination={setPagination !== undefined}
        manualPaginationCount={totalCount}
        stickyHeader
      />
      <EditProductionSiteDrawer
        opened={editedProductionSite !== undefined}
        productionSite={editedProductionSite}
        onClose={() => setEditedProductionSite(undefined)}
        pagination={pagination}
      />
      {deletedProductionSite !== undefined && (
        <DeleteProductionSiteModal
          isOpen={openedDelete}
          onClose={() => {
            closeDelete()
            setDeletedProductionSite(undefined)
          }}
          productionSite={deletedProductionSite}
          pagination={pagination}
        />
      )}
    </>
  )
}
